import type {
	ChatContentBlock,
	ChatMessage,
	ToolResultContentBlock,
	ToolUseContentBlock,
} from '#layers/ai/types/chat';
import type { UiMessage, UiToolContentBlock } from '#layers/ai/types/ui-chat';
import { isToolExecuteContentBlock } from '#layers/ai/types/chat';

const HIDDEN_TOOL_NAMES = new Set(['tool_search', 'tool_describe']);

export function toUiMessages(messages: ChatMessage[]): UiMessage[] {
	const ignoredToolCallIds = collectIgnoredToolCallIds(messages);
	const toolResults = collectVisibleToolResults(messages, ignoredToolCallIds);

	return messages.map((message) => ({
		role: message.role,
		content: toUiContentBlocks(message.content, toolResults, ignoredToolCallIds),
	}));
}

function collectIgnoredToolCallIds(messages: ChatMessage[]): Set<string> {
	const ignoredToolCallIds = new Set<string>();

	for (const message of messages) {
		for (const block of message.content) {
			if (block.type === 'toolUse' && HIDDEN_TOOL_NAMES.has(block.name)) {
				ignoredToolCallIds.add(block.id);
			}
		}
	}

	return ignoredToolCallIds;
}

function collectVisibleToolResults(
	messages: ChatMessage[],
	ignoredToolCallIds: Set<string>,
): Map<string, ToolResultContentBlock> {
	const toolResults = new Map<string, ToolResultContentBlock>();

	for (const message of messages) {
		for (const block of message.content) {
			if (block.type === 'toolResult' && !ignoredToolCallIds.has(block.toolUseId)) {
				toolResults.set(block.toolUseId, block);
			}
		}
	}

	return toolResults;
}

function toUiContentBlocks(
	blocks: ChatContentBlock[],
	toolResults: Map<string, ToolResultContentBlock>,
	ignoredToolCallIds: Set<string>,
): UiMessage['content'] {
	const uiBlocks: UiMessage['content'] = [];

	for (const block of blocks) {
		if (block.type === 'toolResult') continue;
		if (block.type === 'toolApprovalRequest') continue;
		if (block.type === 'toolApprovalResponse') continue;

		if (block.type === 'toolUse') {
			if (ignoredToolCallIds.has(block.id)) continue;

			uiBlocks.push(createUiToolContentBlock(block, toolResults.get(block.id)));
			continue;
		}

		uiBlocks.push(block);
	}

	return uiBlocks;
}

function createUiToolContentBlock(
	block: ToolUseContentBlock,
	result?: ToolResultContentBlock,
): UiToolContentBlock {
	if (isToolExecuteContentBlock(block)) {
		return {
			type: 'uiTool',
			name: block.input?.name ?? block.name,
			intent: block.input?.intent,
			input: block.input?.input ?? block.input ?? {},
			output: result?.content,
			isError: result?.isError,
		};
	}

	return {
		type: 'uiTool',
		name: block.name,
		input: block.input ?? {},
		output: result?.content,
		isError: result?.isError,
	};
}
